import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Alert, Snackbar } from '@mui/material'

const ToastContext = createContext(null)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}

const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null)
  const nextId = useRef(0)

  const notify = useCallback((message, severity = 'error') => {
    if (!message) return
    nextId.current += 1
    setToast({ id: nextId.current, message, severity })
  }, [])

  const value = useMemo(() => ({
    notify,
    error: (message) => notify(message, 'error'),
    success: (message) => notify(message, 'success'),
    info: (message) => notify(message, 'info')
  }), [notify])

  const close = () => setToast(null)

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.id}
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 2, md: 3 } }}
      >
        <Alert
          severity={toast?.severity || 'error'}
          variant='outlined'
          onClose={close}
          sx={{ backgroundColor: 'background.paper', borderColor: 'divider', minWidth: 280 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export default ToastProvider
